import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { LogOut, FileText, Briefcase, Mail, Plus, Edit2, Trash2, X, Save, Loader, ArrowLeft } from 'lucide-react';
import { supabase } from '../lib/supabase';
import './Admin.css';

const ADMIN_EMAIL = 'admin@partsol.site';
const ADMIN_PASSWORD = 'P@aS&Xfi6Dqi8KnEPJHC';

// Check if Supabase is configured
const isSupabaseConfigured = () => {
  const url = process.env.REACT_APP_SUPABASE_URL;
  return url && url !== 'YOUR_SUPABASE_URL' && !url.includes('your-project');
};

// Default press releases for localStorage fallback
const defaultPressReleases = [
  { id: 8, title: 'Partsol Announces Next Generation AI Stem Cells', date: 'January 28, 2025', excerpt: 'Revolutionary technology redefines how AI is designed.', image: 'https://images.unsplash.com/photo-1532187863486-abf9dbad1b69?w=600&h=400&fit=crop', status: 'Published' },
  { id: 7, title: "National Security Leader Stu Shea Joins Partsol's Board", date: 'November 12, 2025', excerpt: 'Expert in defense intelligence joins advisory board.', image: '/press-release/release1.jpeg', status: 'Published' },
  { id: 6, title: 'Partsol Launches Partsol Futures', date: 'June 10, 2025', excerpt: 'Pioneering research and innovation lab launched.', image: 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=600&h=400&fit=crop', status: 'Published' },
];

const Admin = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [activeTab, setActiveTab] = useState('press-releases');
  const [loading, setLoading] = useState(false);
  
  // Data states
  const [pressReleases, setPressReleases] = useState([]);
  const [jobOpenings, setJobOpenings] = useState([]);
  const [contactSubmissions, setContactSubmissions] = useState([]);
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [editingItem, setEditingItem] = useState(null);

  // Check authentication on mount
  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      loadData();
    }
  }, [isAuthenticated]);

  const checkAuth = async () => {
    // Check sessionStorage first
    if (sessionStorage.getItem('adminAuth') === 'true') {
      setIsAuthenticated(true);
      return;
    }
    // Then check Supabase if configured
    if (isSupabaseConfigured()) {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          setIsAuthenticated(true);
        }
      } catch (e) {
        console.log('Supabase auth check failed');
      }
    }
  };

  const loadData = async () => {
    setLoading(true);
    
    if (!isSupabaseConfigured()) {
      // Use localStorage fallback
      const storedPR = localStorage.getItem('admin_press_releases');
      const storedJobs = localStorage.getItem('admin_job_openings');
      const storedContacts = localStorage.getItem('admin_contact_submissions');
      
      setPressReleases(storedPR ? JSON.parse(storedPR) : defaultPressReleases);
      setJobOpenings(storedJobs ? JSON.parse(storedJobs) : []);
      setContactSubmissions(storedContacts ? JSON.parse(storedContacts) : []);
      setLoading(false);
      return;
    }

    try {
      const { data: prData } = await supabase
        .from('press_releases')
        .select('*')
        .order('created_at', { ascending: false });
      if (prData) setPressReleases(prData);

      const { data: jobData } = await supabase
        .from('job_openings')
        .select('*')
        .order('created_at', { ascending: false });
      if (jobData) setJobOpenings(jobData);

      const { data: contactData } = await supabase
        .from('contact_submissions')
        .select('*')
        .order('created_at', { ascending: false });
      if (contactData) setContactSubmissions(contactData);
    } catch (error) {
      console.error('Error loading data:', error);
      // Fallback to localStorage on error
      setPressReleases(JSON.parse(localStorage.getItem('admin_press_releases') || '[]'));
    }
    setLoading(false);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setLoginError('');
    
    if (loginForm.email === ADMIN_EMAIL && loginForm.password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      sessionStorage.setItem('adminAuth', 'true');
    } else {
      setLoginError('Invalid credentials. Please try again.');
    }
    setLoading(false);
  };

  const handleLogout = async () => {
    if (isSupabaseConfigured()) {
      try { await supabase.auth.signOut(); } catch (e) {}
    }
    sessionStorage.removeItem('adminAuth');
    setIsAuthenticated(false);
    navigate('/admin');
  };

  const openAddModal = (type) => {
    setModalType(type);
    setEditingItem(null);
    setShowModal(true);
  };

  const openEditModal = (type, item) => {
    setModalType(type);
    setEditingItem(item);
    setShowModal(true);
  };

  const handleDelete = async (type, id) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      if (type === 'press-release') {
        const updated = pressReleases.filter(item => item.id !== id);
        setPressReleases(updated);
        if (!isSupabaseConfigured()) {
          localStorage.setItem('admin_press_releases', JSON.stringify(updated));
        } else {
          try { await supabase.from('press_releases').delete().eq('id', id); } catch (e) {}
        }
      } else if (type === 'job') {
        const updated = jobOpenings.filter(item => item.id !== id);
        setJobOpenings(updated);
        if (!isSupabaseConfigured()) {
          localStorage.setItem('admin_job_openings', JSON.stringify(updated));
        } else {
          try { await supabase.from('job_openings').delete().eq('id', id); } catch (e) {}
        }
      } else if (type === 'contact') {
        const updated = contactSubmissions.filter(item => item.id !== id);
        setContactSubmissions(updated);
        if (!isSupabaseConfigured()) {
          localStorage.setItem('admin_contact_submissions', JSON.stringify(updated));
        } else {
          try { await supabase.from('contact_submissions').delete().eq('id', id); } catch (e) {}
        }
      }
    }
  };

  const handleSave = async (formData) => {
    if (modalType === 'press-release') {
      if (editingItem) {
        const updatedItem = { ...editingItem, ...formData };
        const updated = pressReleases.map(item => item.id === editingItem.id ? updatedItem : item);
        setPressReleases(updated);
        if (!isSupabaseConfigured()) {
          localStorage.setItem('admin_press_releases', JSON.stringify(updated));
        } else {
          try { await supabase.from('press_releases').update(formData).eq('id', editingItem.id); } catch (e) {}
        }
      } else {
        const newItem = { ...formData, id: Date.now(), status: formData.status || 'Draft', created_at: new Date().toISOString() };
        const updated = [newItem, ...pressReleases];
        setPressReleases(updated);
        if (!isSupabaseConfigured()) {
          localStorage.setItem('admin_press_releases', JSON.stringify(updated));
        } else {
          try { await supabase.from('press_releases').insert(newItem); } catch (e) {}
        }
      }
    } else if (modalType === 'job') {
      if (editingItem) {
        const updatedItem = { ...editingItem, ...formData };
        const updated = jobOpenings.map(item => item.id === editingItem.id ? updatedItem : item);
        setJobOpenings(updated);
        if (!isSupabaseConfigured()) {
          localStorage.setItem('admin_job_openings', JSON.stringify(updated));
        } else {
          try { await supabase.from('job_openings').update(formData).eq('id', editingItem.id); } catch (e) {}
        }
      } else {
        const newItem = { ...formData, id: Date.now(), status: formData.status || 'Active', created_at: new Date().toISOString() };
        const updated = [newItem, ...jobOpenings];
        setJobOpenings(updated);
        if (!isSupabaseConfigured()) {
          localStorage.setItem('admin_job_openings', JSON.stringify(updated));
        } else {
          try { await supabase.from('job_openings').insert(newItem); } catch (e) {}
        }
      }
    }
    setShowModal(false);
  };

  const markContactAsRead = async (id) => {
    const updated = contactSubmissions.map(item => 
      item.id === id ? { ...item, status: 'Read' } : item
    );
    setContactSubmissions(updated);
    if (!isSupabaseConfigured()) {
      localStorage.setItem('admin_contact_submissions', JSON.stringify(updated));
    } else {
      try { 
        await supabase.from('contact_submissions').update({ status: 'Read' }).eq('id', id); 
      } catch (error) {
        console.error('Error updating contact:', error);
      }
    }
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="admin-login-page">
        <nav className="admin-login-nav">
          <Link to="/" className="back-to-home">
            <ArrowLeft size={18} />
            <span>Back to Website</span>
          </Link>
        </nav>
        <div className="admin-login-container">
          <div className="admin-login-header">
            <img src="/partsol-main.png" alt="Partsol" className="admin-logo" />
            <h1>Admin Dashboard</h1>
            <p>Sign in to manage your content</p>
          </div>
          <form onSubmit={handleLogin} className="admin-login-form">
            {loginError && <div className="login-error">{loginError}</div>}
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                value={loginForm.email}
                onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                placeholder="Enter your email"
                required
              />
            </div>
            <div className="form-group">
              <label>Password</label>
              <input
                type="password"
                value={loginForm.password}
                onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                placeholder="Enter your password"
                required
              />
            </div>
            <button type="submit" className="login-btn" disabled={loading}>
              {loading ? <Loader size={18} className="spin" /> : 'Sign In'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Dashboard
  return (
    <div className="admin-dashboard">
      <aside className="admin-sidebar">
        <div className="sidebar-header">
          <img src="/partsol-main.png" alt="Partsol" className="sidebar-logo" />
          <span>Admin Panel</span>
        </div>
        <nav className="sidebar-nav">
          <button 
            className={`nav-item ${activeTab === 'press-releases' ? 'active' : ''}`}
            onClick={() => setActiveTab('press-releases')}
          >
            <FileText size={20} /> Press Releases
          </button>
          <button 
            className={`nav-item ${activeTab === 'jobs' ? 'active' : ''}`}
            onClick={() => setActiveTab('jobs')}
          >
            <Briefcase size={20} /> Job Openings
          </button>
          <button 
            className={`nav-item ${activeTab === 'contacts' ? 'active' : ''}`}
            onClick={() => setActiveTab('contacts')}
          >
            <Mail size={20} /> Contact Submissions
            {contactSubmissions.filter(c => c.status === 'New').length > 0 && (
              <span className="badge">{contactSubmissions.filter(c => c.status === 'New').length}</span>
            )}
          </button>
        </nav>
        <button className="logout-btn" onClick={handleLogout}>
          <LogOut size={20} /> Logout
        </button>
      </aside>

      <main className="admin-main">
        <header className="admin-header">
          <h1>
            {activeTab === 'press-releases' && 'Press Releases'}
            {activeTab === 'jobs' && 'Job Openings'}
            {activeTab === 'contacts' && 'Contact Submissions'}
          </h1>
          {activeTab !== 'contacts' && (
            <button className="add-btn" onClick={() => openAddModal(activeTab === 'press-releases' ? 'press-release' : 'job')}>
              <Plus size={20} /> Add New
            </button>
          )}
        </header>

        <div className="admin-content">
          {/* Press Releases Tab */}
          {activeTab === 'press-releases' && (
            <div className="data-table">
              <table>
                <thead>
                  <tr>
                    <th style={{width: '60px'}}>Image</th>
                    <th>Title</th>
                    <th style={{width: '130px'}}>Date</th>
                    <th style={{width: '90px'}}>Status</th>
                    <th style={{width: '100px'}}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pressReleases.map(item => (
                    <tr key={item.id}>
                      <td>
                        {item.image && (
                          <img 
                            src={item.image} 
                            alt="" 
                            className="table-thumbnail"
                          />
                        )}
                      </td>
                      <td>
                        <div className="pr-title">{item.title}</div>
                        {item.excerpt && <div className="pr-excerpt">{item.excerpt}</div>}
                      </td>
                      <td>{item.date}</td>
                      <td><span className={`status-badge ${item.status?.toLowerCase()}`}>{item.status}</span></td>
                      <td className="actions">
                        <button onClick={() => openEditModal('press-release', item)} title="Edit"><Edit2 size={16} /></button>
                        <button onClick={() => handleDelete('press-release', item.id)} title="Delete"><Trash2 size={16} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Job Openings Tab */}
          {activeTab === 'jobs' && (
            <div className="data-table">
              <table>
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Department</th>
                    <th>Location</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {jobOpenings.map(item => (
                    <tr key={item.id}>
                      <td>{item.title}</td>
                      <td>{item.department}</td>
                      <td>{item.location}</td>
                      <td><span className={`status-badge ${item.status.toLowerCase()}`}>{item.status}</span></td>
                      <td className="actions">
                        <button onClick={() => openEditModal('job', item)}><Edit2 size={16} /></button>
                        <button onClick={() => handleDelete('job', item.id)}><Trash2 size={16} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Contact Submissions Tab */}
          {activeTab === 'contacts' && (
            <div className="data-table">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Message</th>
                    <th>Date</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {contactSubmissions.map(item => (
                    <tr key={item.id} className={item.status === 'New' ? 'unread' : ''}>
                      <td>{item.name}</td>
                      <td>{item.email}</td>
                      <td className="message-cell">{item.message}</td>
                      <td>{item.date}</td>
                      <td><span className={`status-badge ${item.status.toLowerCase()}`}>{item.status}</span></td>
                      <td className="actions">
                        {item.status === 'New' && (
                          <button onClick={() => markContactAsRead(item.id)} title="Mark as Read">✓</button>
                        )}
                        <button onClick={() => handleDelete('contact', item.id)}><Trash2 size={16} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {/* Modal */}
      {showModal && (
        <Modal
          type={modalType}
          item={editingItem}
          onClose={() => setShowModal(false)}
          onSave={handleSave}
        />
      )}
    </div>
  );
};

// Modal Component
const Modal = ({ type, item, onClose, onSave }) => {
  const [formData, setFormData] = useState(item || {});

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{item ? 'Edit' : 'Add'} {type === 'press-release' ? 'Press Release' : 'Job Opening'}</h2>
          <button className="close-btn" onClick={onClose}><X size={24} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          {type === 'press-release' && (
            <>
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={formData.title || ''}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Date *</label>
                  <input
                    type="text"
                    value={formData.date || ''}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    placeholder="e.g., January 28, 2025"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Location</label>
                  <input
                    type="text"
                    value={formData.location || ''}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    placeholder="e.g., Tampa, FL"
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Excerpt (Short Description)</label>
                <textarea
                  value={formData.excerpt || ''}
                  onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                  placeholder="Brief summary shown on the news page..."
                  rows={2}
                />
              </div>
              <div className="form-group">
                <label>Full Content</label>
                <textarea
                  value={formData.content || ''}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  placeholder="Full press release content..."
                  rows={8}
                />
              </div>
              <div className="form-group">
                <label>Image URL</label>
                <input
                  type="text"
                  value={formData.image || ''}
                  onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                  placeholder="e.g., /press-release/image.jpg or https://..."
                />
              </div>
              <div className="form-group">
                <label>Video URL (YouTube)</label>
                <input
                  type="text"
                  value={formData.video_url || ''}
                  onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
                  placeholder="e.g., https://youtu.be/..."
                />
              </div>
              <div className="form-group">
                <label>Status</label>
                <select
                  value={formData.status || 'Draft'}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                >
                  <option value="Draft">Draft</option>
                  <option value="Published">Published</option>
                </select>
              </div>
            </>
          )}
          {type === 'job' && (
            <>
              <div className="form-group">
                <label>Job Title</label>
                <input
                  type="text"
                  value={formData.title || ''}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Department</label>
                <input
                  type="text"
                  value={formData.department || ''}
                  onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Location</label>
                <input
                  type="text"
                  value={formData.location || ''}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Status</label>
                <select
                  value={formData.status || 'Active'}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                >
                  <option value="Active">Active</option>
                  <option value="Closed">Closed</option>
                </select>
              </div>
            </>
          )}
          <div className="modal-actions">
            <button type="button" className="cancel-btn" onClick={onClose}>Cancel</button>
            <button type="submit" className="save-btn"><Save size={16} /> Save</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Admin;
